import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SwitchLeftIcon from '@mui/icons-material/SwitchLeft';
import { Typography } from '@mui/material';
import React, { memo, useCallback, useEffect, useState } from 'react';

import { GetCompanyInferenceResponse } from '~/services/rtkQuery/company/types';

import styles from './styles.module.scss';

interface Props {
  data: GetCompanyInferenceResponse['result']['data']['supporting_graphs']['investors_table'];
}

type SortBy =
  | 'name-asc'
  | 'name-desc'
  | 'rating-asc'
  | 'rating-desc'
  | 'investments-asc'
  | 'investments-desc';

const Component: React.FC<Props> = ({ data }) => {
  const [sortBy, setSortBy] = useState<SortBy>('name-asc');
  const [sortedData, setSortedData] = useState<Props['data']>([]);

  useEffect(() => {
    setSortedData(
      [...data].sort((a, b) => {
        if (sortBy === 'name-asc') {
          return a.investorname.localeCompare(b.investorname);
        }

        if (sortBy === 'name-desc') {
          return b.investorname.localeCompare(a.investorname);
        }

        if (sortBy === 'rating-asc') {
          return a.rating - b.rating;
        }

        if (sortBy === 'rating-desc') {
          return b.rating - a.rating;
        }

        if (sortBy === 'investments-asc') {
          return a.investments - b.investments;
        }

        if (sortBy === 'investments-desc') {
          return b.investments - a.investments;
        }

        return 1;
      }),
    );
  }, [data, sortBy]);

  const toggleSortByName = useCallback(() => {
    setSortBy(prevValue =>
      prevValue !== 'name-asc' && prevValue !== 'name-desc'
        ? 'name-asc'
        : prevValue === 'name-asc'
          ? 'name-desc'
          : 'name-asc',
    );
  }, []);

  const toggleSortByRating = useCallback(() => {
    setSortBy(prevValue =>
      prevValue !== 'rating-asc' && prevValue !== 'rating-desc'
        ? 'rating-asc'
        : prevValue === 'rating-asc'
          ? 'rating-desc'
          : 'rating-asc',
    );
  }, []);

  const toggleSortByInvestments = useCallback(() => {
    setSortBy(prevValue =>
      prevValue !== 'investments-asc' && prevValue !== 'investments-desc'
        ? 'investments-asc'
        : prevValue === 'investments-asc'
          ? 'investments-desc'
          : 'investments-asc',
    );
  }, []);

  if (!data.length) {
    return null;
  }

  return (
    <div className={styles.container}>
      <Typography
        color="var(--text-2-color)"
        fontWeight="700"
        lineHeight="20px"
        mb="12px"
        ml="7px"
        variant="subtitle1"
      >
        Company Investors Ratings
      </Typography>
      <table className={styles.container__table}>
        <thead>
          <tr>
            <th>
              <div>
                <Typography color="var(--text-3-color)" fontWeight="300" mr="16px" variant="body1">
                  Investors
                </Typography>
                <button aria-label="sort" type="button" onClick={toggleSortByName}>
                  {sortBy === 'name-asc' || sortBy === 'name-desc' ? (
                    <PlayArrowIcon
                      sx={{
                        width: '20px',
                        height: '20px',
                        color: 'var(--gray-6-color)',
                        transform: sortBy === 'name-asc' ? 'rotate(270deg)' : 'rotate(90deg)',
                      }}
                    />
                  ) : (
                    <SwitchLeftIcon
                      sx={{
                        color: 'var(--gray-6-color)',
                        transform: 'rotate(270deg)',
                      }}
                    />
                  )}
                </button>
              </div>
            </th>
            <th>
              <div>
                <Typography color="var(--text-3-color)" fontWeight="300" mr="16px" variant="body1">
                  Ratings
                </Typography>
                <button aria-label="sort" type="button" onClick={toggleSortByRating}>
                  {sortBy === 'rating-asc' || sortBy === 'rating-desc' ? (
                    <PlayArrowIcon
                      sx={{
                        width: '20px',
                        height: '20px',
                        color: 'var(--gray-6-color)',
                        transform: sortBy === 'rating-asc' ? 'rotate(270deg)' : 'rotate(90deg)',
                      }}
                    />
                  ) : (
                    <SwitchLeftIcon
                      sx={{
                        color: 'var(--gray-6-color)',
                        transform: 'rotate(270deg)',
                      }}
                    />
                  )}
                </button>
              </div>
            </th>
            <th>
              <div>
                <Typography color="var(--text-3-color)" fontWeight="300" mr="16px" variant="body1">
                  # of Investments
                </Typography>
                <button aria-label="sort" type="button" onClick={toggleSortByInvestments}>
                  {sortBy === 'investments-asc' || sortBy === 'investments-desc' ? (
                    <PlayArrowIcon
                      sx={{
                        width: '20px',
                        height: '20px',
                        color: 'var(--gray-6-color)',
                        transform:
                          sortBy === 'investments-asc' ? 'rotate(270deg)' : 'rotate(90deg)',
                      }}
                    />
                  ) : (
                    <SwitchLeftIcon
                      sx={{
                        color: 'var(--gray-6-color)',
                        transform: 'rotate(270deg)',
                      }}
                    />
                  )}
                </button>
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          {sortedData.map(data => (
            <tr key={data.investorname}>
              <td>
                <Typography color="text.primary" fontWeight="500" lineHeight="20px" variant="body1">
                  {data.investorname}
                </Typography>
              </td>
              <td>
                <Typography color="text.primary" fontWeight="500" lineHeight="20px" variant="body1">
                  {data.rating.toFixed(2)}
                </Typography>
              </td>
              <td>
                <Typography color="text.primary" fontWeight="500" lineHeight="20px" variant="body1">
                  {data.investments}
                </Typography>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export const InvestorsRating = memo(Component);
